import { UserPlusIcon } from "@heroicons/react/24/solid";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@moe/oss/ui/button";
import { Form } from "@moe/oss/ui/form";
import { CharacterService } from "@moe/priv/model/character";
import { DialogConfig } from "@moe/priv/types/types";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, useBlocker, useNavigate, useRouter } from "@tanstack/react-router";
import { useDeleteChar } from "@web/hooks/use-delete-char";
import { useAppContext } from "@web/route-services/root/AppContext";
import { AdvanceFields } from "@web/route-services/studio/AdvanceFields";
import { BaseStudioView } from "@web/route-services/studio/BaseStudioView";
import { DetailsFields } from "@web/route-services/studio/DetailsField";
import { ImageFields } from "@web/route-services/studio/ImageFields";
import { parseMessageExamples } from "@web/route-services/studio/MessageExamplesField";
import { UpdateData, updateDefaults, updateSchema } from "@web/route-services/studio/schema";
import { TokenSummary } from "@web/route-services/studio/TokenSummary";
import { VisibilityFields } from "@web/route-services/studio/VisibilityFields";
import { queryClient } from "@web/routes/__root";
import { useEffect, useState } from "react";
import { useForm, useFormContext } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

type UpdateFormCharacter = CharacterService.UpdateFormCharacter;

export const Route = createFileRoute("/studio/update")({
  component: Update,
  validateSearch: z.object({ cardID: z.number().positive() })
});

function Update() {
  const form = useForm<UpdateData>({
    resolver: zodResolver(updateSchema),
    defaultValues: updateDefaults
  });

  return (
    <BaseStudioView form={form}>
      <Content />
    </BaseStudioView>
  );
}

function Content() {
  const { createDialog } = useAppContext();
  const { access } = useAppContext();
  const { cardID } = Route.useSearch();
  const [isFormDirty, setIsFormDirty] = useState(false);
  const { deleteChar } = useDeleteChar();
  const nav = useNavigate();
  const { history } = useRouter();
  const form = useFormContext<UpdateData>();

  const { data: char } = useQuery({
    queryKey: ["updateFormCharacter", cardID],
    queryFn: async () => {
      const char: UpdateFormCharacter = await access.character.getForUpdate(cardID);
      form.reset({
        title: char.title,
        tagline: char.tagline,
        avatarDisplay: char.avatar,
        bannerDisplay: char.banner,
        name: char.name,
        description: char.description,
        scenario: char.scenario,
        greeting: char.greeting,
        alternativeGreetings: char.alternativeGreetings,
        definitions: char.definitions,
        messageExamples: parseMessageExamples(char.messageExamples),
        isNSFW: char.isNSFW,
        isPrivate: char.isPrivate
      });
      return char;
    }
  });
  const unavailableOK = char && char.messageCount < CharacterService.unavailableOKThreshold;

  const updateMutation = useMutation({
    mutationFn: (data: UpdateData) => {
      if (!char) throw new Error("Char update data is missing!");
      return access.character.update({
        id: char.id,
        title: data.title,
        tagline: data.tagline,
        name: data.name,
        description: data.description,
        scenario: data.scenario,
        greeting: data.greeting,
        definitions: data.definitions,
        messageExamples: JSON.stringify(data.messageExamples),
        alternativeGreetings: data.alternativeGreetings,
        avatar: data.avatar,
        banner: data.banner,
        isNSFW: data.isNSFW,
        isPrivate: data.isPrivate
      });
    },
    onMutate: () => {
      toast.info("Updating Character...");
    },
    onSuccess: (data) => {
      toast.success("Character updated!");
      queryClient.invalidateQueries({ queryKey: ["updateFormCharacter"] });
      history.go(-1);
    },
    onError: (error) => {
      toast.error(`Failed to update Character: ${error.message}`);
    }
  });

  const resetForm = () => {
    if (!char) throw new Error("Character update data is missing");
    form.reset();
  };

  const resetHandler = () => {
    const config: DialogConfig = {
      title: "Reset Form?",
      actionLabel: "Reset",
      description:
        "Are you sure you want to reset the form? All unsaved changes will be lost. This action cannot be undone.",
      onAction: resetForm
    };
    createDialog(config);
  };

  const onSubmit = (data: UpdateData) => {
    if (updateMutation.isPending) {
      console.log("Card creation already in progress");
      return;
    }
    updateMutation.mutate(data);
  };

  useEffect(() => {
    const subscription = form.watch(() => {
      setIsFormDirty(form.formState.isDirty);
    });
    return () => subscription.unsubscribe();
  }, [form]);

  useBlocker({
    shouldBlockFn: () => {
      return new Promise((resolve) => {
        if (isFormDirty && !form.formState.isSubmitSuccessful) {
          const config: DialogConfig = {
            title: "Unsaved Changes",
            actionLabel: "Leave",
            description: "You have unsaved changes. Are you sure you want to leave this page?",
            onAction: () => {
              const confirmConfig: DialogConfig = {
                title: "Really Leave?",
                actionLabel: "Yes Really",
                description: "Just have to make sure...",
                onAction: () => resolve(false),
                onCancel: () => resolve(true)
              };
              setTimeout(() => {
                createDialog(confirmConfig);
              }, 200);
            },
            onCancel: () => resolve(true)
          };
          createDialog(config);
        } else {
          resolve(false);
        }
      });
    }
  });

  return (
    <div className="bg-container-primary border-line relative w-full rounded-2xl border md:w-[45rem] md:min-w-[45rem]">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8 ">
          <ImageFields />

          {/* Character details container */}
          <div className="px-6 pb-6 pt-6">
            <div className="flex flex-col space-y-10">
              <DetailsFields />
              <AdvanceFields />
              <TokenSummary />

              <VisibilityFields unavailableOK={unavailableOK} isForcedNSFW={char && char.isForcedNSFW} />
              <div className="flex justify-between space-x-3 pt-12">
                <Button
                  className="pl-0"
                  variant="clear"
                  disabled={!unavailableOK}
                  type="button"
                  onClick={async () => {
                    if (!char?.id) return;
                    deleteChar(char.id, () => {
                      nav({ to: "/studio/create" });
                    });
                  }}
                >
                  Delete
                </Button>
                <div className="mx-auto flex space-x-3">
                  <Button variant="clear" type="button" onClick={resetHandler}>
                    Reset
                  </Button>
                  <Button
                    type="submit"
                    onClick={() => {
                      if (!form.getValues("avatar")) {
                        toast.error("A character avatar is required.");
                        return;
                      }
                    }}
                  >
                    <UserPlusIcon />
                    Save
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
}
